import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { getAgentList } from '../api/agent/getAgentList';
import { getBrandList } from '../api/brand/getBrandList';
import { BtnPrimary } from '../components/common/Buttons';
import { fetchMultipleAgentLogs } from '../hooks/queries';
import toast from 'react-hot-toast';
import { formatTime } from '../utils';
import { LoaderCircle } from 'lucide-react';

const ExportLogs: React.FC = () => {
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // fetch agents when component mounts
  useEffect(() => {
    async function loadAgents() {
      try {
        const response = await getAgentList();
        if (response.success && response.agents) {
          setAgents(response.agents);
        }
      } catch (error) {
        console.error('Failed to fetch agents:', error);
      }
    }

    loadAgents();
  }, []);

  // List of months
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Generate list of past 5 years
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const filteredAgents = agents.filter((agent) =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExport = async () => {
    if (!selectedAgents.length || !month || !year) return;

    setIsExporting(true);
    try {
      const brandsResponse = await getBrandList();
      if (!brandsResponse.success || !brandsResponse.brands) {
        throw new Error('Failed to fetch brands');
      }
      const allBrands = brandsResponse.brands;

      const selectedAgentObjects = agents.filter((agent) =>
        selectedAgents.includes(agent.kimId)
      );
      const response = await fetchMultipleAgentLogs(
        selectedAgentObjects,
        month,
        year
      );
      if (!response.success) {
        throw new Error('Failed to fetch logs');
      }

      // Process the data for CSV
      const daysInMonth = new Date(
        parseInt(year),
        parseInt(month),
        0
      ).getDate();
      const dates = Array.from({ length: daysInMonth }, (_, i) => {
        const day = (i + 1).toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      });

      // Session columns depend on the busiest day, so rows are joined at the end.
      const rowParts: {
        info: (string | number)[];
        sessions: string[];
        totals: (string | number)[];
      }[] = [];
      const agentTotals: {
        [key: string]: {
          [key: string]: {
            workTime: number;
            activeTime: number;
            idleTime: number;
            breakTime: number;
          };
        };
      } = {};

      // Process data for each agent
      selectedAgentObjects.forEach((agent) => {
        const agentName = agent.name || 'Unknown';

        // Filter logs for this specific agent
        const agentLogs = Array.isArray(response?.data)
          ? response.data.filter((log: any) => log.kimId === agent.kimId) || []
          : (response?.data && agent._id
              ? response.data[agent._id]?.data
              : []) || [];

        // Group logs by brand alias (assignedTo) for quick lookup
        const logsByBrand: { [brandAlias: string]: any[] } = {};
        agentLogs.forEach((log: any) => {
          const brandAlias = log.assignedTo;
          if (!logsByBrand[brandAlias]) {
            logsByBrand[brandAlias] = [];
          }
          logsByBrand[brandAlias].push(log);
        });

        // Find brands this agent belongs to
        const agentBrands = allBrands.filter((brand) =>
          brand.agents?.includes(agent.kimId)
        );

        agentBrands.forEach((brand) => {
          const brandName = brand.name;
          const brandAlias = brand.alias;
          const timeZone =
            agent.brands?.find((b: any) => b.brandId === brand._id)?.timezone ||
            'UTC';
          const brandLogs = logsByBrand[brandAlias || ''] || [];

          if (!agentTotals[agentName]) {
            agentTotals[agentName] = {};
          }
          if (!agentTotals[agentName][brandName]) {
            agentTotals[agentName][brandName] = {
              workTime: 0,
              activeTime: 0,
              idleTime: 0,
              breakTime: 0,
            };
          }

          dates.forEach((date) => {
            const dayLogs: any[] = [];
            const dailyStats = brandLogs.reduce(
              (
                stats: {
                  workTime: number;
                  idleTime: number;
                  breakTime: number;
                },
                log: any
              ) => {
                const localLogDateTime = moment.utc(log.startTime).tz(timeZone);
                if (localLogDateTime.format('YYYY-MM-DD') === date) {
                  dayLogs.push(log);
                  stats.workTime += log.duration || 0;
                  stats.idleTime += log.idleTime || 0;

                  const breakDuration =
                    log.breaks?.reduce((sum: number, breakItem: any) => {
                      return sum + (breakItem.duration || 0);
                    }, 0) || 0;
                  stats.breakTime += breakDuration;
                }
                return stats;
              },
              { workTime: 0, idleTime: 0, breakTime: 0 }
            );

            const totalWorkTime = dailyStats.workTime + dailyStats.breakTime;
            const idleTime = dailyStats.idleTime;
            const activeTime = dailyStats.workTime; // only active hours

            agentTotals[agentName][brandName].workTime += totalWorkTime;
            agentTotals[agentName][brandName].activeTime += activeTime;
            agentTotals[agentName][brandName].idleTime += idleTime;
            agentTotals[agentName][brandName].breakTime += dailyStats.breakTime;

            // Manual adjustments sit at local midnight with no shift or breaks.
            const isSession = (log: any) =>
              !!log.shiftId ||
              !!log.breaks?.length ||
              moment.utc(log.startTime).tz(timeZone).format('HH:mm:ss') !==
                '00:00:00';

            // Flat [in, out, in, out, ...] so it drops straight into the row.
            const sessions = dayLogs
              .filter(isSession)
              .flatMap((log: any) => [
                moment.utc(log.startTime).tz(timeZone).format('HH:mm:ss'),
                log.stopTime
                  ? moment.utc(log.stopTime).tz(timeZone).format('HH:mm:ss')
                  : '',
              ]);

            // Reported separately so elapsed + adjusted still equals Total Work.
            // Rounded to seconds: capping a day can leave a nanosecond log behind.
            const adjustedSecs = Math.round(
              dayLogs
                .filter((log: any) => !isSession(log))
                .reduce((sum: number, log: any) => sum + (log.duration || 0), 0) *
                60
            );

            rowParts.push({
              info: [date, agentName, brandName, timeZone],
              sessions,
              totals: [
                adjustedSecs ? formatTime(adjustedSecs) : '',
                formatTime((totalWorkTime || 0) * 60),
                formatTime((activeTime || 0) * 60),
                formatTime((idleTime || 0) * 60),
                formatTime((dailyStats.breakTime || 0) * 60),
              ],
            });
          });
        });
      });

      // Every row needs the same width, so pad each up to the busiest day.
      const maxSessionCells = rowParts.reduce(
        (max, { sessions }) => Math.max(max, sessions.length),
        2 // always emit at least one In/Out pair
      );
      const sessionHeaders = Array.from({
        length: maxSessionCells / 2,
      }).flatMap((_, i) => [`Session ${i + 1} In`, `Session ${i + 1} Out`]);
      const headers = [
        'Date',
        'Agent Name',
        'Brand',
        'Timezone',
        ...sessionHeaders,
        'Adjusted',
        'Total Work',
        'Active Time',
        'Idle Time',
        'Break Time',
      ];
      const allRows: (string | number)[][] = rowParts.map(
        ({ info, sessions, totals }) => [
          ...info,
          ...sessions,
          ...new Array(maxSessionCells - sessions.length).fill(''),
          ...totals,
        ]
      );

      // Create summary data
      const summaryRows: (string | number)[][] = [
        [],
        [
          'Agent Name',
          'Brand',
          'Total Work',
          'Active Time',
          'Idle Time',
          'Break Time',
        ],
      ];
      for (const agentName in agentTotals) {
        for (const brandName in agentTotals[agentName]) {
          summaryRows.push([
            agentName,
            brandName,
            formatTime(agentTotals[agentName][brandName].workTime * 60),
            formatTime(agentTotals[agentName][brandName].activeTime * 60),
            formatTime(agentTotals[agentName][brandName].idleTime * 60),
            formatTime(agentTotals[agentName][brandName].breakTime * 60),
          ]);
        }
      }

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...allRows.map((row) => row.join(',')),
        ...summaryRows.map((row) => row.join(',')),
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent-logs-${month}-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Logs exported successfully');
      // onExport(selectedAgents, month, year);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export logs');
    } finally {
      setIsExporting(false);
    }
  };

  // Toggle selection of a single agent
  const handleAgentSelection = (agentId: string) => {
    setSelectedAgents((prev) => {
      const isAlreadySelected = prev.includes(agentId);
      return isAlreadySelected
        ? prev.filter((id) => id !== agentId) // Remove if selected
        : [...prev, agentId]; // Add if not selected
    });
  };

  const handleSelectAllAgents = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAgents(
      e.target.checked ? agents.map((agent) => agent.kimId) : []
    );
  };

  // Check if all agents are selected
  const isAllSelected =
    agents.length > 0 && selectedAgents.length === agents.length;

  // Close dropdown when clicking outside
  useEffect(() => {
    function closeDropdownOnOutsideClick(event: MouseEvent) {
      const dropdown = document.getElementById('agent-dropdown');
      if (dropdown && !dropdown.contains(event.target as Node)) {
        setIsAgentDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', closeDropdownOnOutsideClick);
    return () =>
      document.removeEventListener('mousedown', closeDropdownOnOutsideClick);
  }, []);

  return (
    <div className="space-y-4 max-w-5xl md:max-w-6xl mx-auto">
      <h2 className="text-2xl dark:text-gray-200 font-bold my-4 px-2">
        Export Agent Logs (Timezone based on agent settings)
      </h2>
      <div className="flex flex-col gap-4 bg-white p-4 rounded-md border border-greyFifty">
        {/* Agent Selection Dropdown */}
        <div className="relative" id="agent-dropdown">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Select Agents
          </label>
          <div
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer"
            onClick={() => setIsAgentDropdownOpen(!isAgentDropdownOpen)}
          >
            <div className="flex justify-between items-center">
              <span>
                {selectedAgents.length
                  ? `${selectedAgents.length} agent${selectedAgents.length !== 1 ? 's' : ''} selected`
                  : 'Select agents...'}
              </span>
              <svg
                className={`w-5 h-5 transition-transform ${isAgentDropdownOpen ? 'transform rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {/* Dropdown Content */}
          {isAgentDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
              {/* Search Input */}
              <div className="sticky top-0 bg-white dark:bg-gray-700 p-2 border-b border-gray-300 dark:border-gray-600">
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-1 border border-gray-300 text-black dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Select All Option */}
              <div className="p-2 border-b border-gray-300 dark:border-gray-600">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAllAgents}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    Select All
                  </span>
                </label>
              </div>

              {/* Agent List - Now using filteredAgents */}
              <div className="overflow-y-auto max-h-[240px]">
                {filteredAgents.map((agent) => (
                  <label
                    key={agent._id}
                    className="flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAgents.includes(agent.kimId)}
                      onChange={() => handleAgentSelection(agent.kimId)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">
                      {agent.name}
                    </span>
                  </label>
                ))}
                {filteredAgents.length === 0 && (
                  <div className="px-4 py-2 text-sm text-black dark:text-gray-400">
                    No agents found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Month and Year Selection */}
        <div className="grid grid-cols-2 gap-4">
          {/* Month Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select Month</option>
              {months.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {/* Year Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Year
            </label>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select Year</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-2 mt-6">
        <BtnPrimary
          onClick={handleExport}
          disabled={!selectedAgents.length || !month || !year}
        >
          {isExporting ? (
            <div className="flex items-center gap-2">
              <span>Exporting</span>
              <LoaderCircle size={16} className="animate-spin" />
            </div>
          ) : (
            'Export'
          )}
        </BtnPrimary>
      </div>
    </div>
  );
};

export default ExportLogs;
